// FMO Compliance - Cross-agency compliance matrix
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Shield, CheckCircle, AlertTriangle, XCircle, Clock, Search } from 'lucide-react';
import { TourBubble } from '../../components/tour/TourOverlay';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');


const FMO_COMPLIANCE_TOUR_STEPS = [
  { type: 'splash', title: 'Cross-Agency Compliance', description: 'Track license, E&O, and AHIP status for every agent across all your agencies. CMS classifies FMOs as delegated FDRs — this is your compliance safety net.' },
  { target: 'fmo-comp-summary', emoji: '📋', title: 'Compliance Summary', description: 'Total agents, compliant (green), warnings (yellow — expiring in 60 days), and expired/missing (red). Get expired agents to zero as your top priority.', position: 'bottom' },
  { target: 'fmo-comp-search', emoji: '🔍', title: 'Search & Filter', description: 'Search by agent name or agency. Filter by status to quickly find who needs attention. Start with "Expired" filter to focus on the most urgent issues.', position: 'bottom' },
  { target: 'fmo-comp-matrix', emoji: '📊', title: 'Compliance Matrix', description: 'Green/yellow/red dots show license, E&O, and AHIP status per agent. Dates show expiration. This cross-agency view reveals compliance gaps that individual agencies may miss.', position: 'top' },
  { type: 'celebration', title: 'Compliance = Protection!', description: 'CMS fines for FDR non-compliance can reach $25K per violation. Check this weekly and alert agency owners about expiring credentials 60 days in advance.' },
];

function StatusDot({ status }) {
  const colors = { current: 'bg-green-500', warning: 'bg-yellow-500', expired: 'bg-red-500', missing: 'bg-gray-300' };
  return <span className={`inline-block w-3 h-3 rounded-full ${colors[status] || colors.missing}`} title={status} />;
}

function formatDate(d) { return d ? new Date(d).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '--'; }

export default function FmoCompliance() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const [fcTourStep, setFcTourStep] = useState(0);
  const [showFcTour, setShowFcTour] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem('fmo-compliance-tour-dismissed')) setShowFcTour(true);
  }, []);
  const advanceFcTour = useCallback(() => { setFcTourStep(s => { if (s + 1 >= FMO_COMPLIANCE_TOUR_STEPS.length) { localStorage.setItem('fmo-compliance-tour-dismissed', 'true'); setShowFcTour(false); return 0; } return s + 1; }); }, []);
  const backFcTour = useCallback(() => setFcTourStep(s => Math.max(0, s - 1)), []);
  const dismissFcTour = useCallback(() => { localStorage.setItem('fmo-compliance-tour-dismissed', 'true'); setShowFcTour(false); }, []);
  useEffect(() => {
    if (!showFcTour) return;
    const h = (e) => { if (e.key === 'Escape') dismissFcTour(); if (e.key === 'ArrowRight' || e.key === 'Enter') advanceFcTour(); if (e.key === 'ArrowLeft') backFcTour(); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [showFcTour, advanceFcTour, backFcTour, dismissFcTour]);

  const fetchCompliance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/fmo/compliance`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json);
    } catch (err) { console.error('[FMO Compliance]', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCompliance(); }, [fetchCompliance]);

  const now = new Date();
  const warn60 = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);

  const filtered = (data?.agents || []).filter(a => {
    if (search && !a.agentName?.toLowerCase().includes(search.toLowerCase()) && !a.agencyName?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter !== 'all' && a.status !== filter) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/fmo/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#172A3A] flex items-center gap-2"><Shield className="text-blue-600" /> Compliance</h1>
              <p className="text-sm text-gray-500">Cross-agency license, E&O, and AHIP tracking</p>
            </div>
          </div>
          <button onClick={fetchCompliance} className="p-2 hover:bg-gray-100 rounded-lg"><RefreshCw size={18} className="text-gray-500" /></button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {data?.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-tour="fmo-comp-summary">
            <div className="bg-white rounded-xl border border-gray-200 p-4"><p className="text-sm text-gray-500">Total</p><p className="text-2xl font-bold">{data.summary.total}</p></div>
            <div className="bg-white rounded-xl border-2 border-green-200 p-4"><p className="text-sm text-green-600">Compliant</p><p className="text-2xl font-bold text-green-600">{data.summary.compliant}</p></div>
            <div className="bg-white rounded-xl border-2 border-yellow-200 p-4"><p className="text-sm text-yellow-600">Warnings</p><p className="text-2xl font-bold text-yellow-600">{data.summary.warnings}</p></div>
            <div className="bg-white rounded-xl border-2 border-red-200 p-4"><p className="text-sm text-red-600">Expired</p><p className="text-2xl font-bold text-red-600">{data.summary.expired}</p></div>
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap" data-tour="fmo-comp-search">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search agents or agencies..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {[['all','All'],['compliant','OK'],['warning','Warning'],['expired','Expired']].map(([v,l]) => (
              <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 text-sm rounded-md ${filter === v ? 'bg-white shadow font-medium' : 'text-gray-500'}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-x-auto" data-tour="fmo-comp-matrix">
          {loading ? (
            <div className="p-12 text-center"><RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto" /></div>
          ) : (
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Agent</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Agency</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">NPN</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">License</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">E&O</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">AHIP</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => {
                  const licExp = a.licenseExpiration ? new Date(a.licenseExpiration) : null;
                  const eoExp = a.eoExpiration ? new Date(a.eoExpiration) : null;
                  const licStatus = !licExp ? 'missing' : licExp <= now ? 'expired' : licExp < warn60 ? 'warning' : 'current';
                  const eoStatus = !eoExp ? 'missing' : eoExp <= now ? 'expired' : eoExp < warn60 ? 'warning' : 'current';
                  const ahipStatus = a.ahipCertified ? 'current' : 'missing';

                  return (
                    <tr key={`${a.agentId}-${a.agencyId}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3"><p className="font-semibold text-gray-900">{a.agentName}</p><p className="text-xs text-gray-400">{a.email}</p></td>
                      <td className="px-4 py-3 text-sm text-gray-600">{a.agencyName}</td>
                      <td className="px-4 py-3 text-center text-sm font-mono text-gray-600">{a.npn || '--'}</td>
                      <td className="px-4 py-3 text-center"><StatusDot status={licStatus} /><br/><span className="text-[10px] text-gray-400">{formatDate(a.licenseExpiration)}</span></td>
                      <td className="px-4 py-3 text-center"><StatusDot status={eoStatus} /><br/><span className="text-[10px] text-gray-400">{formatDate(a.eoExpiration)}</span></td>
                      <td className="px-4 py-3 text-center"><StatusDot status={ahipStatus} /></td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          a.status === 'compliant' ? 'bg-green-100 text-green-700' :
                          a.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>{a.status === 'compliant' ? 'OK' : a.status === 'warning' ? 'Warn' : 'Action'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showFcTour && (
        <TourBubble stepData={FMO_COMPLIANCE_TOUR_STEPS[fcTourStep]} stepIndex={fcTourStep} totalSteps={FMO_COMPLIANCE_TOUR_STEPS.length}
          onNext={advanceFcTour} onBack={backFcTour} onDismiss={dismissFcTour}
          splashIcon={<Shield className="w-10 h-10 text-blue-600" />} maskId="fmo-compliance-tour-mask" />
      )}
    </div>
  );
}
